import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskLine } from "./Reveal";
import { IMAGES, STATS, COMPANY } from "@/data/site";
import { ArrowDownRight } from "lucide-react";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      {/* Parallax image */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Industrial factory floor" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </motion.div>

      <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 lg:px-10 pb-16 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-primary" />
          <span className="font-mono text-xs tracking-[0.35em] text-primary uppercase">
            Est. 1999 — Sheet Metal Experts
          </span>
        </motion.div>

        <h1 className="font-display font-semibold text-foreground leading-[0.92] text-5xl sm:text-7xl lg:text-[8.5rem] tracking-tight">
          <MaskLine delay={0.25}>PRECISION.</MaskLine>
          <MaskLine delay={0.4} className="text-primary">POWER.</MaskLine>
          <MaskLine delay={0.55}>PERFORMANCE.</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8"
        >
          <p className="max-w-xl text-base lg:text-lg text-foreground/80 leading-relaxed">
            {COMPANY.name} engineers and manufactures high-precision sheet metal components —
            stamping, pressing, fabrication, machining and R&D under one roof.
          </p>
          <a
            href="#capabilities"
            data-testid="hero-cta"
            className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-4 font-semibold self-start hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            Explore Capabilities
            <ArrowDownRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
          </a>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-16 grid grid-cols-2 lg:grid-cols-4 border-t border-white/10"
        >
          {STATS.map((s, i) => (
            <div key={i} className="py-6 px-2 lg:px-6 border-b lg:border-b-0 border-r border-white/10 last:border-r-0">
              <div className="font-display text-3xl lg:text-5xl font-semibold text-foreground">{s.value}</div>
              <div className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase mt-2">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
